/**
 * Rasterize tinted theme SVGs to PNG and upload to S3 on Apply.
 * Preview keeps data URIs; Apply embeds CDN URLs with ?v= cache bust.
 */

import axios from "axios";
import { buildTintedIconSvgs, svgToDataUri } from "./statusFigures";

const UPLOAD_CONCURRENCY = 5;
const PNG_SCALE = 2;

function parseSvgSize(svg) {
    const markup = String(svg || "");
    const w = markup.match(/\bwidth="(\d+(?:\.\d+)?)"/i);
    const h = markup.match(/\bheight="(\d+(?:\.\d+)?)"/i);
    return {
        width: w ? Math.max(1, Math.round(Number(w[1]))) : 24,
        height: h ? Math.max(1, Math.round(Number(h[1]))) : 24,
    };
}

/**
 * @param {string} svgMarkup
 * @param {{ scale?: number, width?: number, height?: number }} [options]
 * @returns {Promise<Blob>}
 */
export function svgToPngBlob(
    svgMarkup,
    { scale = PNG_SCALE, width: widthOverride, height: heightOverride } = {},
) {
    const parsed = parseSvgSize(svgMarkup);
    const width = widthOverride > 0 ? widthOverride : parsed.width;
    const height = heightOverride > 0 ? heightOverride : parsed.height;
    const uri = svgToDataUri(svgMarkup);

    return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => {
            try {
                const canvas = document.createElement("canvas");
                canvas.width = Math.max(1, Math.round(width * scale));
                canvas.height = Math.max(1, Math.round(height * scale));
                const ctx = canvas.getContext("2d");
                if (!ctx) {
                    reject(new Error("Canvas unavailable"));
                    return;
                }
                ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
                canvas.toBlob(blob => {
                    if (!blob) {
                        reject(new Error("PNG encode failed"));
                        return;
                    }
                    resolve(blob);
                }, "image/png");
            } catch (err) {
                reject(err);
            }
        };
        img.onerror = () => reject(new Error("SVG rasterize failed"));
        img.src = uri;
    });
}

function kebabIconName(tokenKey) {
    return String(tokenKey || "")
        .replace(/([a-z0-9])([A-Z])/g, "$1-$2")
        .replace(/_/g, "-")
        .toLowerCase();
}

/**
 * Stable S3 key (overwrite on re-Apply). Matches Medias ACL prefix.
 * @param {{ accountId: string, storeId: string, skinId: string, iconKey: string }}
 */
export function emailThemeIconFileName({
    accountId,
    storeId,
    skinId,
    iconKey,
}) {
    const skin = String(skinId || "brand")
        .toLowerCase()
        .replace(/[^a-z0-9_-]/g, "");
    const name = kebabIconName(iconKey);
    return `${accountId}/${storeId}/email-themes/${skin}/${name}.png`;
}

async function mapPool(items, concurrency, mapper) {
    const results = new Array(items.length);
    let next = 0;

    async function worker() {
        while (next < items.length) {
            const index = next;
            next += 1;
            results[index] = await mapper(items[index], index);
        }
    }

    const workers = Array.from(
        { length: Math.min(concurrency, items.length) },
        () => worker(),
    );
    await Promise.all(workers);
    return results;
}

/**
 * @param {object} options
 * @param {string} options.accountId
 * @param {string} options.storeId
 * @param {string} options.skinId
 * @param {string} options.iconKey
 * @param {Blob} options.blob
 * @param {(args: { variables: { input: { file_name: string, file_type: string } } }) => Promise<any>} options.generatePresignedUrl
 * @returns {Promise<string>} CDN URL without query
 */
export async function uploadEmailThemeIcon({
    accountId,
    storeId,
    skinId,
    iconKey,
    blob,
    generatePresignedUrl,
}) {
    const fileName = emailThemeIconFileName({
        accountId,
        storeId,
        skinId,
        iconKey,
    });
    const fileType = "image/png";
    const resp = await generatePresignedUrl({
        variables: {
            input: {
                file_name: fileName,
                file_type: fileType,
            },
        },
    });
    const { url, headers } = resp?.data?.uploadFile || {};
    if (!url) {
        throw new Error(`Presign failed for ${iconKey}`);
    }

    const config = {
        headers: {
            "Content-Type": fileType,
            ...(Array.isArray(headers)
                ? headers.reduce(
                      (prev, { key, value }) => ({
                          ...prev,
                          [key]: value,
                      }),
                      {},
                  )
                : {}),
        },
    };

    const upload = await axios.put(url, blob, config);
    if (upload.status !== 200) {
        throw new Error(`Upload failed for ${iconKey}`);
    }

    const base = process.env.REACT_APP_STATIC_CONTENT_BASE_URL || "";
    return `${base}${fileName}`;
}

/**
 * Tint → PNG → S3 for icon tokens used by the template. Returns URL map with ?v= cache bust.
 *
 * @param {Record<string, string>} colorTokens expanded theme tokens (no icons yet)
 * @param {object} ctx
 * @param {string} ctx.accountId
 * @param {string} ctx.storeId
 * @param {string} ctx.skinId
 * @param {Function} ctx.generatePresignedUrl Apollo mutate fn
 * @param {Set<string>|null} [onlyKeys] template {{THEME.icon*}} keys; omit to upload all
 * @returns {Promise<Record<string, string>>}
 */
export async function resolveIconUrlsForApply(
    colorTokens,
    ctx,
    onlyKeys = null,
) {
    const { accountId, storeId, skinId, generatePresignedUrl } = ctx || {};
    if (!accountId || !storeId || !skinId || !generatePresignedUrl) {
        throw new Error("Missing upload context for email theme icons");
    }

    const svgs = buildTintedIconSvgs(colorTokens, onlyKeys);
    const entries = Object.entries(svgs);
    if (!entries.length) return {};

    const cacheBust = Date.now();
    const uploaded = await mapPool(
        entries,
        UPLOAD_CONCURRENCY,
        async ([iconKey, svg]) => {
            const blob = await svgToPngBlob(svg);
            const cdnUrl = await uploadEmailThemeIcon({
                accountId,
                storeId,
                skinId,
                iconKey,
                blob,
                generatePresignedUrl,
            });
            return [iconKey, `${cdnUrl}?v=${cacheBust}`];
        },
    );

    return Object.fromEntries(uploaded);
}
